import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyApi } from '../../api/company.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Modal, Badge, EmptyState, Skeleton } from '../../components/ui/index';
import LeafletMapPicker from '../../components/map/LeafletMapPicker';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { MapPin, PencilSimple } from '@phosphor-icons/react';

// Dispatcher/manager_employee could only add a place (AddPlacePage), never
// see or correct one afterward - no list, no edit endpoint. This is that
// missing piece, backed by the new get_locations_list/edit_place endpoints.
export default function PlacesListPage() {
  const navigate = useNavigate();
  const { addToast } = useUI();
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // the location row being edited
  const [form, setForm] = useState({ company_name: '', name: '', lat: '', lng: '', address: '' });
  const [saving, setSaving] = useState(false);

  const load = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    return companyApi.getLocationsList()
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch(() => { if (showSpinner) addToast('خطا در دریافت لیست مکان‌ها', 'error'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    companyApi.getCompanyList().then((r) => setCompanies(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useAutoRefresh(() => load(false), { paused: !!editing });

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      company_name: loc.company_name || '',
      name: loc.name || '',
      lat: String(loc.lat ?? ''),
      lng: String(loc.long ?? ''),
      address: loc.description || '',
    });
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { addToast('نام مکان الزامی است', 'error'); return; }
    setSaving(true);
    try {
      await companyApi.editPlace({
        location_id: editing.location_id,
        // Main office locations have no company - keep it that way instead
        // of accidentally attaching one via an empty-string company_name.
        company_name: editing.is_main_office ? null : (form.company_name || null),
        name: form.name,
        lat: form.lat,
        long: form.lng,
        description: form.address,
      });
      addToast('مکان بروزرسانی شد', 'success');
      setEditing(null);
      load();
    } catch { addToast('خطا در بروزرسانی مکان', 'error'); }
    setSaving(false);
  };

  const inp = { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
  const label = { fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 5 };

  return (
    <div className="fade-in">
      <PageHeader title="مدیریت مکان‌ها" subtitle={`${locations.length} مکان`}
        action={<Btn onClick={() => navigate('/map/add-place')}>+ افزودن مکان</Btn>} />

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2, 3].map((k) => <Skeleton key={k} h={64} radius={12} />)}
        </div>
      ) : locations.length === 0 ? (
        <EmptyState icon={<MapPin size={44} weight="regular" />} title="هنوز مکانی ثبت نشده است" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {locations.map((loc) => (
            <div key={loc.location_id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{loc.name || 'بدون نام'}</span>
                  {loc.is_main_office && <Badge variant="blue">دفتر مرکزی</Badge>}
                  {loc.company_name && <Badge variant="gray">{loc.company_display || loc.company_name}</Badge>}
                </div>
                {loc.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{loc.description}</div>}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, direction: 'ltr', textAlign: 'left' }}>
                  {Number(loc.lat).toFixed(5)}, {Number(loc.long).toFixed(5)}
                </div>
              </div>
              <Btn size="sm" variant="secondary" onClick={() => openEdit(loc)}><PencilSimple size={13} weight="regular" /> ویرایش</Btn>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="ویرایش مکان" width={560}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!editing?.is_main_office && (
            <div>
              <label style={label}>شرکت</label>
              <select value={form.company_name} onChange={set('company_name')} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">-- بدون شرکت --</option>
                {companies.map((c) => <option key={c.id} value={c.name}>{c.display_name || c.name}</option>)}
              </select>
            </div>
          )}
          <div><label style={label}>نام مکان</label><input value={form.name} onChange={set('name')} style={inp} /></div>
          <div>
            <label style={label}>موقعیت روی نقشه</label>
            <LeafletMapPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => setForm((p) => ({ ...p, lat: String(lat), lng: String(lng) }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={label}>عرض جغرافیایی</label><input value={form.lat} onChange={set('lat')} dir="ltr" style={inp} /></div>
            <div><label style={label}>طول جغرافیایی</label><input value={form.lng} onChange={set('lng')} dir="ltr" style={inp} /></div>
          </div>
          <div><label style={label}>آدرس</label><textarea value={form.address} onChange={set('address')} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => setEditing(null)}>انصراف</Btn>
            <Btn onClick={save} loading={saving}>ذخیره تغییرات</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
